import { createMutation, useIsMutating } from '@tanstack/svelte-query'
import { get } from 'svelte/store'
import queryClient from '../../../constants/query-client'
import { setFileLoadingError } from '../../../stores/file-loading'
import FileLoader from '../file-loader'
import { focusOnObject } from '../../../stores/camera'
import useCanvasSelection from '../../../hooks/canvas-selection'
import { pendingPlacement } from '../../../stores/placement'
import { preferredPrinter } from '../../../stores/preferred-printer'
import { storeScene } from '../../../stores/scene'
import type { Group } from 'three'
import { PrinterService } from '../../../services'

const MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024 // 200 MB

const useUploadedFilesQueries = () => {
  const { upsert: selectObject } = useCanvasSelection()

  const loadInputFilesToScene = async (files: FileList | File[] | null) => {
    if (!files?.length) {
      console.debug('Input files or scene is null')
      return
    }

    const groups: Group[] = []
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileLoadingError(file.name, 'fileTooLarge')
        continue
      }

      const loader = new FileLoader(file)
      const group = await loader.load()
      if (group) groups.push(group)
    }

    if (!groups.length) return

    const finalizePlacement = () => {
      queryClient.invalidateQueries({ queryKey: ['canvas'] })
      const lastGroup = groups[groups.length - 1]
      const target = lastGroup.children.length === 1 ? lastGroup.children[0] : lastGroup
      selectObject(target)
      focusOnObject(target)
    }

    const preferredPrinterValue = get(preferredPrinter)
    if (preferredPrinterValue && PrinterService.getPrinterDriver(preferredPrinterValue).hasGhostPlacement) {
      pendingPlacement.set({
        groups,
        type: 'file',
        onPlaced: finalizePlacement,
      })
    }
    else {
      const scene = get(storeScene)
      for (const group of groups) scene?.add(group)
      finalizePlacement()
    }
  }

  const uploadFileMutationKey = ['fileUpload']

  const useFileUploadIsMutating = (): { current: number } => useIsMutating({
    mutationKey: uploadFileMutationKey,
  }, queryClient)

  const uploadFileMutation = createMutation(() => ({
    mutationKey: ['fileUpload'],
    mutationFn: loadInputFilesToScene,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canvas'] })
    },
  }), () => queryClient)

  return {
    uploadFileMutation,
    useFileUploadIsMutating,
  }
}

export default useUploadedFilesQueries
